import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, Radar, Trash2 } from 'lucide-react';
import type { DashboardContainer, DepotCheckResponse, DepotOption } from '@ctp/shared';
import { dndTone, type DndTone } from '../lib/premium';
import { DepotCell } from './DepotCell';
import { ContainerDetailModal } from './ContainerDetailModal';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from './ui';

const TONE_CLASS: Record<DndTone, string> = {
  ok: 'bg-status-ok/15 text-status-ok',
  warn: 'bg-status-terminal/15 text-status-terminal',
  breach: 'bg-status-problem/15 text-status-problem',
};

/**
 * Demurrage-/Detention-Countdown mit Ampel (grün/gelb/rot). Richtwert-Daten (A-110).
 * `ended` = Phase abgeschlossen (A-118): statt eines weitertickenden Countdowns wird „beendet"
 * (im Limit geblieben) bzw. die tatsächliche Überziehung angezeigt.
 */
function DndChip({
  label,
  daysLeft,
  ended,
}: {
  label: string;
  daysLeft?: number | null;
  ended?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  if (daysLeft == null) {
    return (
      <span className="inline-flex flex-col">
        <span className="text-[10px] uppercase text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{t('premium.dnd.noData')}</span>
      </span>
    );
  }
  const tone: DndTone = ended ? (daysLeft < 0 ? 'breach' : 'ok') : dndTone(daysLeft);
  const toneLabel =
    tone === 'breach'
      ? t('premium.dnd.breach')
      : tone === 'warn'
        ? t('premium.dnd.warn')
        : t('premium.dnd.ok');
  const text = ended
    ? daysLeft < 0
      ? t('premium.dnd.endedExceeded', { days: -daysLeft })
      : t('premium.dnd.ended')
    : t('premium.dnd.remaining', { days: daysLeft });
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className="text-[10px] uppercase text-muted-foreground">{label}</span>
      <span
        className={`inline-flex w-fit items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${TONE_CLASS[tone]}`}
        title={toneLabel}
      >
        {text}
      </span>
    </span>
  );
}

/** Kompakte Datums-Zelle mit Label (Milestone). */
function DateCell({
  label,
  iso,
  fmt,
}: {
  label: string;
  iso?: string | null;
  fmt: (s: string | null) => string;
}): JSX.Element {
  return (
    <span className="inline-flex flex-col">
      <span className="text-[10px] uppercase text-muted-foreground">{label}</span>
      <span className="text-xs">{fmt(iso ?? null)}</span>
    </span>
  );
}

/** Kompaktes Filter-Eingabefeld in der Kopf-Filterzeile. */
function FilterInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}): JSX.Element {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-7 px-2 text-xs font-normal"
    />
  );
}

/**
 * Eigene Premium-Container-Tabelle ÜBER der Standard-Tabelle (D7). Zeigt Milestones (Löschung/
 * Gate-Out/Leerabgabe), ETA/ETD, Demurrage/Detention-Countdown mit Ampel, ein Leerdepot-Dropdown
 * und Aktionen (Löschen). Ein Klick auf die Zeile öffnet die Detailansicht (alle Milestones +
 * Terminal-/Reise-Infos). Eine Zeile leuchtet kurz auf (`premium-flash`), wenn ihre ID in `flashedIds`
 * steht. Filterzeile analog Standard-Tabelle. White-Label: keine Quellen-/Anbieter-Nennung.
 */
export function PremiumTable({
  containers,
  flashedIds,
  fmtDate,
  token,
  depots,
  depotSel,
  depotResult,
  depotChecking,
  onDepotSelect,
  onRequestDelete,
  canDelete,
  deletingId,
  onRequestComplete,
  completingId,
}: {
  containers: DashboardContainer[];
  flashedIds: Set<string>;
  fmtDate: (iso: string | null) => string;
  token?: string | null;
  depots: DepotOption[];
  depotSel: Record<string, string>;
  depotResult: Record<string, DepotCheckResponse>;
  depotChecking: Record<string, boolean>;
  onDepotSelect: (c: DashboardContainer, depotId: string, reference?: string) => void;
  onRequestDelete: (c: DashboardContainer) => void;
  canDelete: (c: DashboardContainer) => boolean;
  deletingId: string | null;
  /** Manueller Abschluss (EPIC Q, optional): archiviert die Org-Sicht — Zeile wandert ins Archiv. */
  onRequestComplete?: (c: DashboardContainer) => void;
  completingId?: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ number: '', vesselPort: '', eta: '', updated: '' });
  const [detail, setDetail] = useState<{ id: string; number: string } | null>(null);

  const visible = useMemo(() => {
    const inc = (hay: string, needle: string): boolean =>
      hay.toLowerCase().includes(needle.trim().toLowerCase());
    return containers.filter((c) => {
      if (filters.number && !inc(c.containerNumber, filters.number)) return false;
      if (filters.vesselPort && !inc(`${c.vessel ?? ''} ${c.port ?? ''}`, filters.vesselPort))
        return false;
      if (filters.eta && !inc(fmtDate(c.eta), filters.eta)) return false;
      if (filters.updated && !inc(fmtDate(c.lastRefreshedAt), filters.updated)) return false;
      return true;
    });
  }, [containers, filters, fmtDate]);

  if (containers.length === 0) {
    return (
      <div data-testid="premium-empty">
        <EmptyState title={t('premium.empty')} />
      </div>
    );
  }

  return (
    <Card data-testid="premium-table">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Radar className="h-5 w-5 text-accent-foreground" />
          {t('premium.tableTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <THead>
            <TR>
              <TH>{t('dashboard.col.number')}</TH>
              <TH>{t('dashboard.col.vesselPort')}</TH>
              <TH>{t('premium.milestones.discharged')}</TH>
              <TH>{t('premium.milestones.gateOut')}</TH>
              <TH>{t('premium.milestones.emptyReturned')}</TH>
              <TH>
                {t('premium.milestones.eta')} / {t('premium.milestones.etd')}
              </TH>
              <TH>
                {t('premium.dnd.demurrage')} / {t('premium.dnd.detention')}
              </TH>
              <TH>{t('premium.updated')}</TH>
              <TH>{t('dashboard.col.depot')}</TH>
              <TH className="text-right">{t('dashboard.col.action')}</TH>
            </TR>
            <TR className="[&>th]:pb-2 [&>th]:pt-0 hover:bg-transparent">
              <TH>
                <FilterInput
                  value={filters.number}
                  onChange={(v) => setFilters((f) => ({ ...f, number: v }))}
                  placeholder={t('dashboard.filterPlaceholder')}
                />
              </TH>
              <TH>
                <FilterInput
                  value={filters.vesselPort}
                  onChange={(v) => setFilters((f) => ({ ...f, vesselPort: v }))}
                  placeholder={t('dashboard.filterPlaceholder')}
                />
              </TH>
              <TH />
              <TH />
              <TH />
              <TH>
                <FilterInput
                  value={filters.eta}
                  onChange={(v) => setFilters((f) => ({ ...f, eta: v }))}
                  placeholder={t('dashboard.filterPlaceholder')}
                />
              </TH>
              <TH />
              <TH>
                <FilterInput
                  value={filters.updated}
                  onChange={(v) => setFilters((f) => ({ ...f, updated: v }))}
                  placeholder={t('dashboard.filterPlaceholder')}
                />
              </TH>
              <TH />
              <TH />
            </TR>
          </THead>
          <TBody>
            {visible.length === 0 ? (
              <TR>
                <TD colSpan={10} className="py-6 text-center text-muted-foreground">
                  {t('dashboard.noFilterMatch')}
                </TD>
              </TR>
            ) : (
              visible.map((c) => (
                <TR
                  key={c.trackedContainerId}
                  data-testid={`premium-row-${c.trackedContainerId}`}
                  className={`cursor-pointer ${flashedIds.has(c.trackedContainerId) ? 'premium-flash' : ''}`}
                  onClick={() => setDetail({ id: c.trackedContainerId, number: c.containerNumber })}
                  title={t('premium.detail.open')}
                >
                  <TD className="font-mono">
                    {c.containerNumber}
                    {c.sizeType && (
                      <span
                        className="ml-1.5 text-xs text-muted-foreground"
                        title={c.isoType ? `ISO ${c.isoType}` : undefined}
                      >
                        {c.sizeType}
                      </span>
                    )}
                  </TD>
                  <TD>
                    <span
                      className="block max-w-[170px] truncate"
                      title={[c.vessel, c.port].filter(Boolean).join(' · ') || undefined}
                    >
                      {c.vessel ?? '—'}
                      {c.port && <span className="text-muted-foreground"> · {c.port}</span>}
                    </span>
                  </TD>
                  <TD>
                    <DateCell
                      label={t('premium.milestones.discharged')}
                      iso={c.dischargedAt}
                      fmt={fmtDate}
                    />
                  </TD>
                  <TD>
                    <DateCell
                      label={t('premium.milestones.gateOut')}
                      iso={c.gateOutAt}
                      fmt={fmtDate}
                    />
                  </TD>
                  <TD>
                    <DateCell
                      label={t('premium.milestones.emptyReturned')}
                      iso={c.emptyReturnedAt}
                      fmt={fmtDate}
                    />
                  </TD>
                  <TD>
                    <div className="flex flex-col">
                      <span className="text-xs">
                        {t('premium.milestones.eta')}: {fmtDate(c.eta)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t('premium.milestones.etd')}: {fmtDate(c.etd)}
                      </span>
                    </div>
                  </TD>
                  <TD>
                    <div className="flex gap-3">
                      <DndChip
                        label={t('premium.dnd.demurrage')}
                        daysLeft={c.demurrageDaysLeft}
                        ended={c.demurrageEnded}
                      />
                      <DndChip
                        label={t('premium.dnd.detention')}
                        daysLeft={c.detentionDaysLeft}
                        ended={c.detentionEnded}
                      />
                    </div>
                  </TD>
                  <TD className="whitespace-nowrap text-muted-foreground">
                    {fmtDate(c.lastRefreshedAt)}
                  </TD>
                  <TD>
                    <DepotCell
                      depots={depots}
                      selectedId={depotSel[c.trackedContainerId]}
                      result={depotResult[c.trackedContainerId]}
                      checking={!!depotChecking[c.trackedContainerId]}
                      onSelect={(depotId, reference) => onDepotSelect(c, depotId, reference)}
                    />
                  </TD>
                  <TD className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Badge variant="purple">{t('premium.badge')}</Badge>
                      {onRequestComplete && canDelete(c) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('archive.completeAction')}
                          aria-label={t('archive.completeAction')}
                          disabled={completingId === c.trackedContainerId}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRequestComplete(c);
                          }}
                          className="text-muted-foreground"
                        >
                          {completingId === c.trackedContainerId ? (
                            <Spinner />
                          ) : (
                            <Archive className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {canDelete(c) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('dashboard.delete')}
                          aria-label={t('dashboard.delete')}
                          disabled={deletingId === c.trackedContainerId}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRequestDelete(c);
                          }}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          {deletingId === c.trackedContainerId ? (
                            <Spinner />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </CardContent>

      <ContainerDetailModal
        containerId={detail?.id ?? null}
        containerNumber={detail?.number ?? null}
        token={token}
        onClose={() => setDetail(null)}
        fmtDate={fmtDate}
      />
    </Card>
  );
}
